import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCopyToClipboard } from 'usehooks-ts';

import { Loader } from '@/components/loader';
import { Tooltip } from '@/components/tooltip';
import { IconButton } from '@/components/button';
import { Tabs } from '@/components/tabs';
import { convertModelTypes } from '@/explore/input';
import { useTitle } from '@/lib/hooks/use-title';
import { useTrackEvent } from '@/lib/analytics';
import { useAccountContext } from '@/lib/accounts/context';

import { useAccountSettingsQuery } from '../graphql';
import { AccessTokensSection } from './access-tokens';
import { CliSection } from './cli/section';
import { UsersSection } from './users/section';
import { ConnectionsSection } from './connections/section';
import { IntegrationsSection } from './integrations/section';
import { NavigationSection } from './navigation/section';
import { ModelsSection } from './models/section';
import { AlertsSection } from './alerts';
import { AiBusinessConceptsSettings } from './ai-business-concepts';

import styles from './settings.module.scss';

export const AccountSettingsPage = () => {
  useTitle('Account Settings');
  const { account, hasPermission } = useAccountContext();
  const [accountIdCopied, setAccountIdCopied] = useState(false);
  const trackEvent = useTrackEvent();
  const [, copyToClipboard] = useCopyToClipboard();
  const { view } = useParams();
  const navigate = useNavigate();

  const { data, refetch } = useAccountSettingsQuery({
    fetchPolicy: 'cache-and-network',
    variables: { accountId: account.accountId },
  });

  const handleCopyAccountId = async () => {
    await copyToClipboard(account.accountId);

    setAccountIdCopied(true);
    trackEvent('Account ID Copied', { accountIdId: account.accountId });

    setTimeout(() => setAccountIdCopied(false), 3000);
  };

  const menuItems = useMemo(
    () => [
      {
        label: 'General',
        href: '/settings/general',
      },
      {
        label: 'Alerts',
        href: '/settings/alerts',
      },
      {
        label: 'Data Models',
        href: '/settings/models',
      },
      {
        label: 'Integrations',
        href: '/settings/integrations',
      },
      {
        label: 'AI',
        href: '/settings/ai',
      },
      {
        label: 'Developer',
        href: '/settings/developers',
      },
    ],
    [],
  );

  const activeMenuItemIdx = menuItems.findIndex(
    (item) => item.href.split('/settings/').pop() === view,
  );

  useEffect(() => {
    if (activeMenuItemIdx === -1) {
      return navigate('/settings/general', { replace: true });
    }
  }, [navigate, activeMenuItemIdx]);

  return (
    <div className={styles.wrapper}>
      <h1>Account Settings</h1>

      <nav className={styles.menu}>
        <Tabs items={menuItems} activeIndex={activeMenuItemIdx} />
      </nav>

      <div className={styles.sections}>
        {data === undefined ? (
          <Loader />
        ) : view === 'general' || view === undefined ? (
          <>
            <UsersSection
              accountId={account.accountId}
              currentUserId={data?.account?.me.userId ?? ''}
              canEdit={hasPermission('MANAGE_USERS')}
              users={data?.account?.users ?? []}
            />
            <NavigationSection
              navigation={data?.account?.navigation ?? []}
              explorations={data?.account?.explorations ?? []}
              canEdit={hasPermission('MANAGE_ACCOUNT')}
            />
          </>
        ) : view === 'developers' ? (
          <>
            <div className={styles.accountId}>
              Account ID: <pre>{account.accountId}</pre>
              <Tooltip content={accountIdCopied ? 'Copied!' : 'Copy Account ID'}>
                <IconButton
                  icon="Copy"
                  onClick={handleCopyAccountId}
                  onMouseLeave={() => setTimeout(() => setAccountIdCopied(false), 500)}
                />
              </Tooltip>
            </div>
            <AccessTokensSection accessTokens={data?.accessTokens ?? []} refetch={refetch} />
            <CliSection />
          </>
        ) : view === 'integrations' ? (
          <>
            <ConnectionsSection connections={data?.account?.connections ?? []} />
            <IntegrationsSection integrations={data?.account?.integrations ?? []} />
          </>
        ) : view === 'models' ? (
          <ModelsSection models={convertModelTypes(data.account?.models) ?? []} />
        ) : view === 'alerts' ? (
          <AlertsSection
            accountId={account.accountId}
            alertConfigurations={data?.account?.alertConfigurations ?? []}
            models={convertModelTypes(data?.account?.models) ?? []}
            metrics={data?.account?.metrics ?? []}
            integrations={data?.account?.integrations ?? []}
            refetch={refetch}
            canEdit={hasPermission('MANAGE_ALERTS')}
          />
        ) : view === 'ai' && hasPermission('MANAGE_AI_SYSTEM_INSTRUCTIONS') ? (
          <AiBusinessConceptsSettings
            aiBusinessConcepts={data?.account?.aiBusinessConcepts ?? ''}
            accountName={data?.account?.name}
            integrations={data?.account?.integrations ?? []}
            connections={data?.account?.connections ?? []}
            aiSystemInstructions={data?.account?.aiSystemInstructions ?? []}
          />
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};
